import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ArrowRight, Copy, Check, ChevronRight, Zap, Shield, Globe,
  FlaskConical, Cpu, Database, Bot, Star, ExternalLink,
  TrendingUp, Activity, Microscope, Atom, Beaker,
  Building2, GraduationCap, Factory, Leaf, Battery, TestTube,
  ChevronDown, Layers, Workflow, Sparkles, Rocket,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { SCIENCE_LLMS } from "@/lib/data";
import { API_PUBLIC_BASE_URL, ANTHROPIC_PROXY_URL } from "@/const";

// ─── Design Philosophy ────────────────────────────────────────────────────────
// Dark-first scientific aesthetic. Space Grotesk for display, system-ui for body.
// Color palette: deep navy (#0A0F1E) bg, electric blue (#2563EB), cyan (#06B6D4).
// Layout: asymmetric, left-anchored hero, full-bleed sections with subtle grid.
// Motion: snappy ease-out (cubic-bezier(0.23, 1, 0.32, 1)), GPU-only transforms.
// ─────────────────────────────────────────────────────────────────────────────

const CODE_EXAMPLES = {
  python: `import anthropic

client = anthropic.Anthropic(
    api_key="YOUR_SCIENCETOKEN_API_KEY",
    base_url="${ANTHROPIC_PROXY_URL}"
)

# Call Science Standard LLM
message = client.messages.create(
    model="xtalpi-science-standard",
    max_tokens=1024,
    messages=[{
        "role": "user",
        "content": "Predict the logP of: CC(=O)Oc1ccccc1C(=O)O"
    }]
)

print(message)`,
  curl: `curl ${ANTHROPIC_PROXY_URL}/v1/messages \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "anthropic-version: 2023-06-01" \\
  -d '{
    "model": "xtalpi-science-standard",
    "max_tokens": 1024,
    "messages": [{
      "role": "user",
      "content": "Predict logP for: CC(=O)Oc1ccccc1C(=O)O"
    }]
  }'`,
  nodejs: `import Anthropic from '@anthropic-ai/sdk';

const client = new Anthropic({
  apiKey: process.env.SCIENCETOKEN_API_KEY,
  baseURL: '${ANTHROPIC_PROXY_URL}',
});

const message = await client.messages.create({
  model: 'xtalpi-science-standard',
  max_tokens: 1024,
  messages: [{
    role: 'user',
    content: 'Predict logP for: CC(=O)Oc1ccccc1C(=O)O',
  }],
});

console.log(message);`,
};

function CodeBlock() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<"python" | "curl" | "nodejs">("python");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(CODE_EXAMPLES[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-white/3">
        <div className="flex gap-1">
          {(["python", "curl", "nodejs"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "px-3 py-1 rounded text-xs font-medium transition-all",
                activeTab === tab
                  ? "bg-blue-500/20 text-blue-400"
                  : "text-slate-500 hover:text-slate-300"
              )}
            >
              {tab === "nodejs" ? "Node.js" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          {copied ? t("common.copied") : t("common.copy")}
        </button>
      </div>
      <div className="p-4 overflow-x-auto">
        <pre className="text-xs leading-relaxed font-mono">
          <code className="text-slate-300">{CODE_EXAMPLES[activeTab]}</code>
        </pre>
      </div>
    </div>
  );
}

function AnimatedCounter({ target, suffix = "" }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 1800;
          const step = target / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

// Proprietary algorithms to highlight on homepage
const XTALPI_ALGORITHMS = [
  {
    id: "xtalpi/xtalfold",
    name: "XtalFold™",
    icon: <Microscope size={20} className="text-blue-400" />,
    domain: "Protein Structure",
    domainZh: "蛋白质结构预测",
    tagline: "Atomic-level accuracy in 1 day",
    taglineZh: "1天内原子级精度预测",
    color: "blue",
  },
  {
    id: "xtalpi/xfep",
    name: "XFEP",
    icon: <Atom size={20} className="text-cyan-400" />,
    domain: "Free Energy Perturbation",
    domainZh: "自由能微扰计算",
    tagline: "Wet-lab accuracy, AI speed",
    taglineZh: "湿实验精度，AI速度",
    color: "cyan",
  },
  {
    id: "xtalpi/xmolgen",
    name: "XMolGen",
    icon: <Beaker size={20} className="text-emerald-400" />,
    domain: "Molecular Generation",
    domainZh: "分子生成与设计",
    tagline: "10M+ novel scaffolds generated",
    taglineZh: "已生成1000万+新型分子骨架",
    color: "emerald",
  },
  {
    id: "xtalpi/xtalgazer-csp",
    name: "XtalGazer™ CSP",
    icon: <Database size={20} className="text-violet-400" />,
    domain: "Crystal Structure Prediction",
    domainZh: "晶型预测",
    tagline: "100% accuracy in Pfizer blind test",
    taglineZh: "辉瑞盲测100%准确率",
    color: "violet",
  },
  {
    id: "xtalpi/reaction-predictor",
    name: "Reaction Predictor",
    icon: <FlaskConical size={20} className="text-amber-400" />,
    domain: "Organic Synthesis",
    domainZh: "有机合成化学",
    tagline: "20+ reaction types, 2M+ training data",
    taglineZh: "20+反应类型，200万+训练数据",
    color: "amber",
  },
  {
    id: "xtalpi/admet-predictor",
    name: "ADMET Predictor",
    icon: <Activity size={20} className="text-rose-400" />,
    domain: "Drug-likeness & Toxicity",
    domainZh: "成药性与毒性预测",
    tagline: "50+ endpoints, wet-lab accuracy",
    taglineZh: "50+终点，湿实验级准确率",
    color: "rose",
  },
  {
    id: "xtalpi/materials-predictor",
    name: "Materials Predictor",
    icon: <Zap size={20} className="text-orange-400" />,
    domain: "New Energy Materials",
    domainZh: "新能源材料科学",
    tagline: "Perovskite, PV, solid electrolytes",
    taglineZh: "钙钛矿、光伏、固态电解质",
    color: "orange",
  },
];

// Physical World workstations
const PHYSICAL_WORLD_STATIONS = [
  {
    name: "XmartChem®",
    nameZh: "智能合成工作站",
    domain: "Drug Synthesis",
    domainZh: "有机化学合成",
    icon: <FlaskConical size={24} className="text-blue-400" />,
    desc: "Fully automated chemical synthesis with integrated LC-MS analysis. 100% experiment traceability.",
    descZh: "全自动化学合成，集成在线LC-MS分析，100%实验可追溯。",
    color: "blue",
    image: "/labs/xmartchem.png",
  },
  {
    name: "Catalyst Station",
    nameZh: "催化剂合成工作站",
    domain: "Catalysis R&D",
    domainZh: "催化剂研发",
    icon: <Atom size={24} className="text-cyan-400" />,
    desc: "High-throughput catalyst synthesis with automated solid/liquid dispensing and high-temperature control.",
    descZh: "高通量催化剂合成，自动化固液加样，高温控制合成反应。",
    color: "cyan",
    image: "/labs/catalyst.webp",
  },
  {
    name: "Electrolyte Station",
    nameZh: "智能电解液工作站",
    domain: "New Energy / Battery",
    domainZh: "新能源/电池",
    icon: <Battery size={24} className="text-emerald-400" />,
    desc: "AI formulation design → robotic execution → rapid performance testing. Full pipeline automation.",
    descZh: "AI配方设计→机器人执行→性能快速检测，全流程自动化。",
    color: "emerald",
    image: "/labs/electrolyte.webp",
  },
  {
    name: "TCM Lab",
    nameZh: "中药智能化实验室",
    domain: "Traditional Chinese Medicine",
    domainZh: "中药现代化",
    icon: <Leaf size={24} className="text-teal-400" />,
    desc: "World's first unmanned TCM drug development platform. Automated separation and analysis of TCM components.",
    descZh: "全球首个\"一站式、无人化\"中药新药创制平台，中药成分全自动分离与分析。",
    color: "teal",
    image: "/labs/tcm.png",
  },
  {
    name: "Formulation Station",
    nameZh: "配方稳定性测试工作站",
    domain: "Chemical / Formulation",
    domainZh: "化工/配方研发",
    icon: <TestTube size={24} className="text-violet-400" />,
    desc: "Automated sample handling, barcode scanning, pH & viscosity testing. Deployed globally at BASF.",
    descZh: "自动化样品上下样、扫码、pH及粘度测试全流程。已在巴斯夫全球落地。",
    color: "violet",
    image: "/labs/formulation.webp",
  },
  {
    name: "ChemPlus®",
    nameZh: "桌面固体分散仪",
    domain: "Universal Lab",
    domainZh: "通用实验室",
    icon: <Beaker size={24} className="text-amber-400" />,
    desc: "1mg-20g range, ±0.5mg precision. AI self-optimizing parameters, high-throughput unattended operation.",
    descZh: "1mg~20g范围，±0.5mg精度，AI自优化分散参数，高通量无人值守操作。",
    color: "amber",
    image: "/labs/chemplus.webp",
  },
];

export default function Home() {
  const { language, t } = useLanguage();
  const { platformTheme } = useTheme();
  const isDark = platformTheme === "tech";
  const [showFoundation, setShowFoundation] = useState(false);

  const STATS = [
    { value: "200+", label: language === "en" ? "Proprietary Models" : "自研专有算法", icon: <FlaskConical size={16} className="text-blue-400" /> },
    { value: "1M", label: t("home.stats.context"), icon: <Zap size={16} className="text-cyan-400" /> },
    { value: "99.9%", label: t("home.stats.uptime"), icon: <Shield size={16} className="text-emerald-400" /> },
  ];

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-foreground">
      <Navbar />

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <section className="relative min-h-[100vh] flex flex-col overflow-hidden">
        {/* Full-bleed background image */}
        <div className="absolute inset-0">
          <img
            src={
              isDark
                ? "https://d2xsxph8kpxj0f.cloudfront.net/310519663549861870/AvPSjvZMopPJiTBbj8oDQi/hero-dark-v3-9RDQ9AhWXUaKTF7Gft6Q8r.webp"
                : "https://d2xsxph8kpxj0f.cloudfront.net/310519663549861870/AvPSjvZMopPJiTBbj8oDQi/hero-light-v3-VmvWAAMhzF7kZ5wQBXkpV7.webp"
            }
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: "center center" }}
          />
          {isDark ? (
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E] via-[#0A0F1E]/85 to-[#0A0F1E]/30" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E] via-transparent to-[#0A0F1E]/50" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-white/30" />
            </>
          )}
        </div>

        <div className="absolute inset-0 sci-bg-grid opacity-40" />

        {/* Main hero content */}
        <div className="relative flex-1 flex items-center">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
            <div className="max-w-[680px]">
              {/* Civilization declaration badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium mb-6">
                <span className="status-dot" style={{ width: 6, height: 6 }} />
                {t("home.badge")}
                <ChevronRight size={12} />
              </div>

              {/* Main headline */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-4"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                <span className={isDark ? "text-white" : "text-slate-900"}>Science,</span>
                <br />
                <span className="sci-gradient-text">Tokenized.</span>
              </h1>

              {/* Sub-headline: civilization statement */}
              <p
                className={cn(
                  "text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug tracking-tight mb-5",
                  isDark ? "text-slate-300" : "text-slate-600"
                )}
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                {language === "en"
                  ? "A New Age of Scientific Discovery"
                  : "科学大航海时代"}
              </p>

              <p className={cn(
                "text-sm sm:text-base leading-relaxed mb-8 max-w-[560px]",
                isDark ? "text-slate-400" : "text-slate-500"
              )}>
                {language === "en"
                  ? "We encapsulate XtalPi's decade of AI for Science into callable Tokens — so anyone, anywhere, can wield the same capabilities trusted by the world's top institutions. No lab required. Just curiosity and an API key."
                  : "我们将晶泰科技十年积累的 AI for Science 能力封装为可调用的 Token——让任何人、在任何地方，都能使用全球顶级机构信赖的同款科学计算能力。不需要实验室，只需要好奇心和一个 API Key。"}
              </p>

              {/* API Endpoint Display */}
              <div className={cn(
                "flex items-center gap-3 p-3 rounded-xl border mb-8 max-w-[460px]",
                isDark ? "bg-black/30 border-white/10 backdrop-blur-sm" : "bg-white/80 border-slate-200 backdrop-blur-sm"
              )}>
                <span className={cn("text-xs font-mono", isDark ? "text-slate-500" : "text-slate-400")}>base_url</span>
                <code className="flex-1 text-sm font-mono text-cyan-500">
                  {API_PUBLIC_BASE_URL}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(ANTHROPIC_PROXY_URL)}
                  className={cn("transition-colors", isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600")}
                >
                  <Copy size={13} />
                </button>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-4">
                <Link href="/console/keys">
                  <Button size="lg" className="btn-primary-sci gap-2 text-base px-6 font-semibold h-12">
                    {t("home.getApiKey")}
                    <ArrowRight size={16} />
                  </Button>
                </Link>
                <Link href="/llms">
                  <Button
                    size="lg"
                    variant="ghost"
                    className={cn(
                      "gap-2 text-base px-6 h-12 font-medium border transition-all",
                      isDark
                        ? "btn-outline-sci"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100 bg-white/60 backdrop-blur-sm"
                    )}
                  >
                    {t("home.exploreModels")}
                    <ChevronRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className={cn(
          "relative border-t backdrop-blur-md",
          isDark ? "border-white/5 bg-[#0A0F1E]/80" : "border-slate-200/60 bg-white/75"
        )}>
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {STATS.map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                    isDark ? "bg-white/5" : "bg-slate-100"
                  )}>
                    {stat.icon}
                  </div>
                  <div>
                    <div
                      className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")}
                      style={{ fontFamily: "Space Grotesk, sans-serif" }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-xs text-slate-500">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Science LLM + Proprietary Algorithm Foundation ─────────── */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* ── Top Tier: Science LLM ── */}
          <div className="mb-16">
            <Badge className="mb-4 bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
              Science LLM
            </Badge>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div>
                <h2
                  className={cn("text-3xl sm:text-4xl font-bold mb-3", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                >
                  {language === "en" ? "Science LLM — Your Direct Interface" : "Science LLM — 用户直接调用的科学大模型"}
                </h2>
                <p className={cn("text-base max-w-[600px]", isDark ? "text-slate-400" : "text-slate-600")}>
                  {language === "en"
                    ? "XtalPi Science Flagship and XtalPi Science Standard are the models you call directly. They natively understand SMILES, FASTA, InChI and other scientific formats, powered by 200+ proprietary algorithms behind the scenes."
                    : "XtalPi Science Flagship 和 XtalPi Science Standard 是您直接调用的模型。它们原生理解 SMILES、FASTA、InChI 等科学格式，背后由 200+ 自研专有算法提供强大支撑。"}
                </p>
              </div>
              <Link href="/llms">
                <Button variant="ghost" className={cn("gap-2 flex-shrink-0", isDark ? "btn-outline-sci" : "border border-slate-300 text-slate-700 hover:bg-slate-50")}>
                  {language === "en" ? "View All Models" : "查看全部模型"}
                  <ArrowRight size={14} />
                </Button>
              </Link>
            </div>

            {/* Two featured Science LLM cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SCIENCE_LLMS.filter(m => m.tier === "Flagship" || m.tier === "Standard").map((model) => {
                const isFlag = model.tier === "Flagship";
                return (
                  <div
                    key={model.id}
                    className={cn(
                      "sci-card p-7 transition-all duration-300 relative overflow-hidden",
                      isFlag
                        ? "border-blue-500/30 hover:border-blue-500/50 sci-glow"
                        : "border-cyan-500/20 hover:border-cyan-500/40",
                      isDark ? "" : "bg-white"
                    )}
                  >
                    {isFlag && (
                      <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                    )}
                    <div className="relative">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold border",
                            isFlag
                              ? "bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400"
                              : "bg-gradient-to-br from-cyan-500/15 to-emerald-500/15 border-cyan-500/25 text-cyan-400"
                          )}>
                            {model.providerShort}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3
                                className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-900")}
                                style={{ fontFamily: "Space Grotesk, sans-serif" }}
                              >
                                {model.name}
                              </h3>
                              {isFlag && <Star size={14} className="text-amber-400 fill-amber-400" />}
                            </div>
                            <p className="text-xs text-slate-500">{model.provider}</p>
                          </div>
                        </div>
                        <span className={cn(
                          "text-[10px] px-2 py-0.5 rounded-full border font-medium",
                          isFlag
                            ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                            : "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"
                        )}>
                          {model.tier}
                        </span>
                      </div>

                      <p className={cn("text-sm leading-relaxed mb-5", isDark ? "text-slate-300" : "text-slate-600")}>
                        {language === "en" ? model.descriptionEn : model.description}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {model.tags.map((tag) => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-white/5 border border-white/10 text-slate-400">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center gap-3">
                        <Link href="/console/llms">
                          <Button size="sm" className="btn-primary-sci text-xs px-4 h-9 gap-1.5">
                            {language === "en" ? "Try Now" : "立刻体验"} <ArrowRight size={12} />
                          </Button>
                        </Link>
                        <code className={cn("text-xs font-mono", isDark ? "text-slate-500" : "text-slate-400")}>{model.id}</code>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Foundation trigger button ── */}
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setShowFoundation(!showFoundation)}
              className={cn(
                "group flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-300",
                isDark
                  ? "bg-white/5 border-white/10 text-slate-400 hover:border-blue-500/30 hover:text-blue-400 hover:bg-blue-500/5"
                  : "bg-slate-50 border-slate-200 text-slate-500 hover:border-blue-400/40 hover:text-blue-500 hover:bg-blue-50"
              )}
            >
              <Layers size={15} className="group-hover:text-blue-400 transition-colors" />
              {language === "en" ? "Discover the Powerful Foundation Behind ScienceToken" : "了解 ScienceToken 的强大基座支撑"}
              <ChevronDown
                size={14}
                className={cn(
                  "transition-transform duration-300",
                  showFoundation && "rotate-180"
                )}
              />
            </button>
          </div>

          {/* ── Bottom Tier: Proprietary Algorithm Foundation (collapsible) ── */}
          <div
            className={cn(
              "overflow-hidden transition-all duration-500 ease-in-out",
              showFoundation ? "max-h-[2000px] opacity-100 mt-12" : "max-h-0 opacity-0 mt-0"
            )}
          >
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-slate-500/10 text-slate-400 border-slate-500/20 text-xs">
                {language === "en" ? "Foundation Layer" : "算法基座"}
              </Badge>
              <h3
                className={cn("text-2xl sm:text-3xl font-bold mb-3", isDark ? "text-white" : "text-slate-900")}
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                {language === "en" ? "200+ Proprietary Algorithms Behind the Scenes" : "200+ 自研专有算法，强大的幕后基座"}
              </h3>
              <p className={cn("text-sm max-w-[600px] mx-auto", isDark ? "text-slate-500" : "text-slate-500")}>
                {language === "en"
                  ? "These proprietary models form the computational foundation of Science LLM. From protein structure prediction to matter prediction, they empower every response."
                  : "这些自研专有算法是 Science LLM 背后的计算基座。从蛋白质结构预测到物质预测，它们为每一次响应注入专业能力。"}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {XTALPI_ALGORITHMS.map((algo) => {
                const colorMap: Record<string, string> = {
                  blue: "border-blue-500/15 hover:border-blue-500/30",
                  cyan: "border-cyan-500/15 hover:border-cyan-500/30",
                  emerald: "border-emerald-500/15 hover:border-emerald-500/30",
                  violet: "border-violet-500/15 hover:border-violet-500/30",
                  amber: "border-amber-500/15 hover:border-amber-500/30",
                  rose: "border-rose-500/15 hover:border-rose-500/30",
                  orange: "border-orange-500/15 hover:border-orange-500/30",
                };
                return (
                  <div
                    key={algo.id}
                    className={cn(
                      "sci-card p-4 transition-all duration-300 group cursor-default",
                      colorMap[algo.color] || "border-white/10"
                    )}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br",
                        `from-${algo.color}-500/15 to-${algo.color}-600/5`
                      )}>
                        {algo.icon}
                      </div>
                      <div>
                        <h4
                          className={cn("text-sm font-bold leading-tight", isDark ? "text-white" : "text-slate-900")}
                          style={{ fontFamily: "Space Grotesk, sans-serif" }}
                        >
                          {algo.name}
                        </h4>
                        <p className="text-[10px] text-slate-500 leading-tight">
                          {language === "en" ? algo.domain : algo.domainZh}
                        </p>
                      </div>
                    </div>
                    <p className={cn("text-[11px] leading-relaxed", isDark ? "text-slate-500" : "text-slate-500")}>
                      {language === "en" ? algo.tagline : algo.taglineZh}
                    </p>
                  </div>
                );
              })}

              {/* "More" card */}
              <Link href="/llms">
                <div className={cn(
                  "sci-card p-4 transition-all duration-300 group cursor-pointer border-dashed flex flex-col items-center justify-center min-h-[100px] hover:border-blue-500/30",
                  isDark ? "border-white/10" : "border-slate-200"
                )}>
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <ChevronRight size={16} className="text-slate-500 group-hover:text-blue-400 transition-colors" />
                  </div>
                  <p className="text-xs text-slate-500 group-hover:text-blue-400 transition-colors text-center">
                    {language === "en" ? "200+ More" : "200+ 更多"}
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Science, Tokenized — 一切科学能力，皆为 Token ──────────── */}
      <section className="py-24 bg-[var(--page-bg-alt)]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-xs">
              Science, Tokenized
            </Badge>
            <h2
              className={cn("text-3xl sm:text-4xl font-bold mb-4", isDark ? "text-white" : "text-slate-900")}
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              {language === "en" ? (
                <>All Science Capabilities as <code className="text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded text-3xl">Token</code></>
              ) : (
                <>一切科学能力，皆为 <code className="text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded text-3xl">Token</code></>
              )}
            </h2>
            <p className={cn("text-base max-w-[640px] mx-auto", isDark ? "text-slate-400" : "text-slate-600")}>
              {language === "en"
                ? "ScienceToken encapsulates XtalPi's decade of AI for Science capabilities — from matter prediction to robotic lab experiments — into standardized, callable Tokens. One API key, unified access, precise metering."
                : "ScienceToken 将晶泰科技十年积累的 AI for Science 能力——从物质预测到机器人本体实验——封装为标准化、可调用的 Token。一个 API Key，统一访问，精确计量。"}
            </p>
          </div>

          {/* Token layers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {/* Layer 1: Science LLM */}
            <div className={cn(
              "sci-card p-7 transition-all duration-300 relative overflow-hidden border-blue-500/20 hover:border-blue-500/40",
              isDark ? "" : "bg-white"
            )}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className={cn(
                  "w-11 h-11 rounded-xl flex items-center justify-center mb-4",
                  isDark ? "bg-blue-500/15" : "bg-blue-50"
                )}>
                  <Cpu size={22} className="text-blue-400" />
                </div>
                <h3
                  className={cn("text-lg font-bold mb-2", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                >
                  {language === "en" ? "Science LLM as Token" : "Science LLM as Token"}
                </h3>
                <p className={cn("text-sm leading-relaxed mb-4", isDark ? "text-slate-400" : "text-slate-600")}>
                  {language === "en"
                    ? "Call XtalPi Science Flagship or Standard via API. Every scientific query — from molecular property prediction to retrosynthesis planning — is metered in Tokens."
                    : "通过 API 调用 XtalPi Science Flagship 或 Standard。每一次科学查询——从分子性质预测到逆合成规划——均以 Token 精确计量。"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {["SMILES", "FASTA", "InChI", "Multi-modal"].map(tag => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Layer 2: Algorithm as Token */}
            <div className={cn(
              "sci-card p-7 transition-all duration-300 relative overflow-hidden border-cyan-500/20 hover:border-cyan-500/40",
              isDark ? "" : "bg-white"
            )}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className={cn(
                  "w-11 h-11 rounded-xl flex items-center justify-center mb-4",
                  isDark ? "bg-cyan-500/15" : "bg-cyan-50"
                )}>
                  <Layers size={22} className="text-cyan-400" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3
                    className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-900")}
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {language === "en" ? "Algorithm as Token" : "Algorithm as Token"}
                  </h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 font-semibold">
                    {language === "en" ? "Coming Soon" : "即将上线"}
                  </span>
                </div>
                <p className={cn("text-sm leading-relaxed mb-4", isDark ? "text-slate-400" : "text-slate-600")}>
                  {language === "en"
                    ? "200+ proprietary algorithms — crystal structure prediction, free energy perturbation, molecular generation — are being progressively tokenized, accessible through the same unified API."
                    : "200+ 自研专有算法——物质结构预测、自由能微扰、分子生成——正在逐步 Token 化，通过同一套统一 API 即可访问。"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {["XtalFold™", "XFEP", "XMolGen", "200+"].map(tag => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Layer 3: Physical World as Token (Coming Soon) */}
            <div className={cn(
              "sci-card p-7 transition-all duration-300 relative overflow-hidden border-emerald-500/20 hover:border-emerald-500/40",
              isDark ? "" : "bg-white"
            )}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className={cn(
                  "w-11 h-11 rounded-xl flex items-center justify-center mb-4",
                  isDark ? "bg-emerald-500/15" : "bg-emerald-50"
                )}>
                  <Workflow size={22} className="text-emerald-400" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3
                    className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-900")}
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {language === "en" ? "Physical World as Token" : "Physical World as Token"}
                  </h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-semibold">
                    {language === "en" ? "Coming Soon" : "即将上线"}
                  </span>
                </div>
                <p className={cn("text-sm leading-relaxed mb-4", isDark ? "text-slate-400" : "text-slate-600")}>
                  {language === "en"
                    ? "Robotic lab experiments — chemical synthesis, formulation testing, catalyst screening — will be tokenized. Submit a protocol via API, receive real experimental data."
                    : "机器人本体实验——化学合成、配方测试、催化剂筛选——即将被 Token 化。通过 API 提交实验方案，获取真实实验数据。"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {["XmartChem®", "ChemPlus®", "Robotics"].map(tag => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Token evolution cycle */}
          <div className={cn(
            "rounded-2xl border p-8 relative overflow-hidden",
            isDark ? "bg-white/2 border-white/8" : "bg-slate-50 border-slate-200"
          )}>
            <div className="absolute inset-0 sci-bg-grid opacity-10" />
            <div className="relative">
              <div className="text-center mb-8">
                <p className={cn("text-xs tracking-widest uppercase mb-3", isDark ? "text-slate-600" : "text-slate-400")}>
                  {language === "en" ? "How it works" : "运作方式"}
                </p>
                <h3
                  className={cn("text-2xl font-bold mb-2", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                >
                  {language === "en" ? "One ScienceToken, Continuously Evolving" : "一种科学词元（ScienceToken），驱动科学持续进化"}
                </h3>
                <p className={cn("text-sm max-w-[600px] mx-auto", isDark ? "text-slate-400" : "text-slate-600")}>
                  {language === "en"
                    ? "ScienceToken (ST) is the unified token powering every step of the science loop — from knowledge analysis to robotic lab experiments. Each cycle generates new knowledge, feeding the next cycle to be smarter."
                    : "ScienceToken (ST) 是驱动科学闭环每一步的统一词元——从知识分析到机器人本体实验。每一轮循环产生新知识，反哺下一轮更智能。"}
                </p>
              </div>

              {/* Infinite scrolling cycle strip */}
              <div className="relative overflow-hidden py-4">
                {/* Fade edges */}
                <div className={cn(
                  "absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none",
                  isDark ? "bg-gradient-to-r from-[#0d1117] to-transparent" : "bg-gradient-to-r from-slate-50 to-transparent"
                )} />
                <div className={cn(
                  "absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none",
                  isDark ? "bg-gradient-to-l from-[#0d1117] to-transparent" : "bg-gradient-to-l from-slate-50 to-transparent"
                )} />

                {/* Scrolling track — 4 steps repeated 4 times = seamless loop */}
                <div className="flex animate-scroll-loop" style={{ width: "fit-content" }}>
                  {Array.from({ length: 4 }).map((_, rep) =>
                    [
                      { icon: <Sparkles size={20} className="text-purple-400" />, label: language === "en" ? "Knowledge Analysis" : "知识分析", bg: isDark ? "bg-purple-500/15" : "bg-purple-50", border: "border-purple-500/25" },
                      { icon: <Cpu size={20} className="text-blue-400" />, label: language === "en" ? "Idea Generation" : "想法生成", bg: isDark ? "bg-blue-500/15" : "bg-blue-50", border: "border-blue-500/25" },
                      { icon: <Atom size={20} className="text-cyan-400" />, label: language === "en" ? "Molecular Design" : "分子设计", bg: isDark ? "bg-cyan-500/15" : "bg-cyan-50", border: "border-cyan-500/25" },
                      { icon: <Database size={20} className="text-violet-400" />, label: language === "en" ? "Matter Prediction" : "物质预测", bg: isDark ? "bg-violet-500/15" : "bg-violet-50", border: "border-violet-500/25" },
                      { icon: <Layers size={20} className="text-amber-400" />, label: language === "en" ? "Property Prediction" : "性质预测", bg: isDark ? "bg-amber-500/15" : "bg-amber-50", border: "border-amber-500/25" },
                      { icon: <Workflow size={20} className="text-emerald-400" />, label: language === "en" ? "Robotic Lab Experiment" : "机器人本体实验", bg: isDark ? "bg-emerald-500/15" : "bg-emerald-50", border: "border-emerald-500/25" },
                      { icon: <TrendingUp size={20} className="text-rose-400" />, label: language === "en" ? "Data Analysis" : "数据分析", bg: isDark ? "bg-rose-500/15" : "bg-rose-50", border: "border-rose-500/25" },
                    ].map((step, i) => (
                      <div key={`${rep}-${i}`} className="flex items-center flex-shrink-0">
                        <div className={cn(
                          "flex items-center gap-3 px-5 py-3.5 rounded-xl border",
                          isDark ? "bg-white/3" : "bg-white",
                          step.border
                        )}>
                          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", step.bg)}>
                            {step.icon}
                          </div>
                          <span className={cn("text-sm font-semibold whitespace-nowrap", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                            {step.label}
                          </span>
                        </div>
                        <ArrowRight size={16} className={cn("mx-3 flex-shrink-0", isDark ? "text-slate-600" : "text-slate-300")} />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* CTA */}
              <div className="flex justify-center mt-6">
                <Link href="/console/keys">
                  <Button size="lg" className="btn-primary-sci gap-2 px-6 h-12 font-semibold">
                    {language === "en" ? "Get API Key" : "获取 API Key"}
                    <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Impact Stories ───────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
              {language === "en" ? "Impact Stories" : "标志性案例"}
            </Badge>
            <h2
              className={cn("text-3xl sm:text-4xl font-bold mb-4", isDark ? "text-white" : "text-slate-900")}
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              {language === "en" ? "Proven Science, Now Being Tokenized" : "经过验证的科学能力，正在被 Token 化"}
            </h2>
            <p className={cn("text-base max-w-[600px] mx-auto", isDark ? "text-slate-400" : "text-slate-600")}>
              {language === "en"
                ? "The algorithms behind these milestones are being progressively encapsulated into ScienceToken — along with our growing automation capabilities."
                : "这些里程碑背后的算法正在逐步封装为 ScienceToken——我们不断增长的自动化能力也将陆续上线。"}
            </p>
          </div>

          {/* Impact grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Paxlovid + 17/20 -- combined left column */}
            <div className="flex flex-col gap-5">
              {/* Paxlovid -- streamlined */}
              <div className={cn(
                "sci-card p-6 border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300",
                isDark ? "" : "bg-white"
              )}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={cn(
                    "h-8 px-3 rounded-lg border flex items-center",
                    isDark ? "bg-white/10 border-white/10" : "bg-white border-slate-200"
                  )}>
                    <img
                      src="/logos/pfizer.png"
                      alt="Pfizer"
                      className={cn("h-5 object-contain", isDark ? "brightness-[2]" : "")}
                    />
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-medium">
                    Paxlovid
                  </span>
                </div>
                <p className={cn("text-sm leading-relaxed mb-4", isDark ? "text-slate-300" : "text-slate-600")}>
                  {language === "en"
                    ? "XtalPi's core algorithms — matter structure prediction — supported the accelerated development of Pfizer's Paxlovid. These same capabilities will soon be accessible via ScienceToken."
                    : "晶泰科技的核心算法——物质结构预测——支撑了辉瑞 Paxlovid 的加速研发。这些能力未来将通过 ScienceToken 开放访问。"}
                </p>
                <div className={cn(
                  "flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg",
                  isDark ? "bg-cyan-500/5 text-cyan-400 border border-cyan-500/15" : "bg-cyan-50 text-cyan-600 border border-cyan-200"
                )}>
                  <Rocket size={13} />
                  {language === "en"
                    ? "Core algorithms being tokenized for ScienceToken"
                    : "核心算法正在 Token 化，即将登陆 ScienceToken"}
                </div>
              </div>

              {/* 17/20 Pharma */}
              <div className={cn(
                "sci-card p-6 border-blue-500/20 hover:border-blue-500/40 transition-all duration-300",
                isDark ? "" : "bg-white"
              )}>
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div
                      className="text-5xl font-black sci-gradient-text"
                      style={{ fontFamily: "Space Grotesk, sans-serif" }}
                    >
                      <AnimatedCounter target={17} />
                      <span className="text-2xl text-slate-500">/20</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {language === "en" ? "Top Pharma Companies" : "全球前20大药企"}
                    </p>
                  </div>
                  <div>
                    <h3
                      className={cn("text-base font-bold mb-2", isDark ? "text-white" : "text-slate-900")}
                      style={{ fontFamily: "Space Grotesk, sans-serif" }}
                    >
                      {language === "en" ? "Trusted by the World's Best" : "全球顶级药企的共同选择"}
                    </h3>
                    <p className={cn("text-xs leading-relaxed mb-2", isDark ? "text-slate-400" : "text-slate-600")}>
                      {language === "en"
                        ? "17 of the world's top 20 pharmaceutical companies rely on XtalPi's algorithms. These enterprise-proven capabilities are progressively becoming available as ScienceTokens."
                        : "全球前20大药企中的17家使用晶泰科技的算法。这些经过企业级验证的能力正逐步封装为 ScienceToken。"}
                    </p>
                    <div className="flex items-center gap-3 mt-3">
                      <div className={cn(
                        "h-7 px-2.5 rounded-lg border flex items-center",
                        isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                      )}>
                        <img
                          src="/logos/pfizer.png"
                          alt="Pfizer"
                          className={cn("h-4 object-contain", isDark ? "brightness-[2] opacity-70" : "opacity-80")}
                        />
                      </div>
                      <div className={cn(
                        "h-7 px-2.5 rounded-lg border flex items-center",
                        isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                      )}>
                        <img
                          src="/logos/lilly.webp"
                          alt="Eli Lilly"
                          className={cn("h-4 object-contain", isDark ? "brightness-[1.5] opacity-70" : "opacity-80")}
                        />
                      </div>
                      <span className={cn("text-xs", isDark ? "text-slate-600" : "text-slate-400")}>
                        {language === "en" ? "+ 15 more" : "+ 15家"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: MIT + BASF + Vision */}
            <div className="flex flex-col gap-5">
              {/* MIT Fellowship -- ScienceToken ecosystem */}
              <div className={cn(
                "sci-card p-6 border-violet-500/20 hover:border-violet-500/40 transition-all duration-300 relative overflow-hidden",
                isDark ? "" : "bg-white"
              )}>
                <div className="absolute top-0 right-0 w-40 h-40 bg-violet-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={cn(
                      "w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 overflow-hidden p-1.5",
                      isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                    )}>
                      <img
                        src="/logos/mit.png"
                        alt="MIT"
                        className="w-full h-full object-contain"
                        style={{ objectPosition: "left center" }}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3
                          className={cn("text-base font-bold", isDark ? "text-white" : "text-slate-900")}
                          style={{ fontFamily: "Space Grotesk, sans-serif" }}
                        >
                          MIT AI for Science Fellowship
                        </h3>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/20">
                          {language === "en" ? "Ecosystem" : "生态布局"}
                        </span>
                      </div>
                      <p className={cn("text-sm leading-relaxed mb-3", isDark ? "text-slate-300" : "text-slate-600")}>
                        {language === "en"
                          ? "XtalPi established the 'XtalPi AI for Science Fellowship' at MIT's Department of Chemistry, funding 4-5 graduate students annually. These outstanding researchers are contributing to the ScienceToken ecosystem — building the next generation of scientific AI capabilities together."
                          : "晶泰科技在 MIT 化学系设立'XtalPi AI for Science Fellowship'，每年资助 4~5 名研究生。这些优秀的科研工作者正在合力打造 ScienceToken 生态——共同构建下一代科学 AI 能力。"}
                      </p>
                      <div className={cn(
                        "flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg",
                        isDark ? "bg-violet-500/5 text-violet-400 border border-violet-500/15" : "bg-violet-50 text-violet-600 border border-violet-200"
                      )}>
                        <Sparkles size={13} />
                        {language === "en"
                          ? "Co-building the ScienceToken ecosystem"
                          : "合力共建 ScienceToken 生态"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BASF */}
              <div className={cn(
                "sci-card p-6 border-amber-500/20 hover:border-amber-500/40 transition-all duration-300",
                isDark ? "" : "bg-white"
              )}>
                <div className="flex items-start gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 overflow-hidden p-0.5",
                    isDark ? "bg-[#004A96] border-white/10" : "bg-[#004A96] border-slate-200"
                  )}>
                    <img
                      src="/logos/basf.webp"
                      alt="BASF"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3
                        className={cn("text-sm font-bold", isDark ? "text-white" : "text-slate-900")}
                        style={{ fontFamily: "Space Grotesk, sans-serif" }}
                      >
                        BASF Automated Lab
                      </h3>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
                        {language === "en" ? "Chemical" : "化工行业"}
                      </span>
                    </div>
                    <p className={cn("text-xs leading-relaxed", isDark ? "text-slate-400" : "text-slate-600")}>
                      {language === "en"
                        ? "XtalPi co-developed an automated formulation stability testing workstation with BASF. This automation capability will be tokenized as part of the Physical World as Token initiative."
                        : "晶泰科技与全球最大化工企业巴斯夫共同开发了自动化配方稳定性测试工作站。这些自动化能力将作为 Physical World as Token 计划的一部分被 Token 化。"}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── Physical World ───────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0">
          {isDark ? (
            <>
              <div className="absolute inset-0 bg-[#0A0F1E]" />
              <div className="absolute inset-0 sci-bg-grid opacity-20" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
              <div className="absolute inset-0 sci-bg-grid opacity-5" />
            </>
          )}
        </div>

        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-xs">
              {language === "en" ? "Physical World as Token" : "Physical World as Token"}
            </Badge>
            <h2
              className={cn("text-3xl sm:text-4xl font-bold mb-4", isDark ? "text-white" : "text-slate-900")}
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              {language === "en" ? "Beyond Algorithms. Into the Physical World" : "超越算法，进入物理世界"}
            </h2>
            <p className={cn("max-w-[700px] mx-auto text-base leading-relaxed", isDark ? "text-slate-400" : "text-slate-600")}>
              {language === "en"
                ? "Cyber Tokens leverage LLMs and domain-specific AI to organize the world's existing scientific knowledge — but existing knowledge has its limits. Physical Tokens, powered by Physical AI and robotic labs running 24/7, explore the unknown and generate real experimental data and new discoveries — the engine that changes the world. From Cyber to Physical, from cognition to experiment, ScienceToken closes the loop of science."
                : "Cyber Token 借助 LLM 与 Domain-specific AI，整合世界已有的科学知识——但存量知识终有边界。Physical Token 依托 Physical AI 驱动的机器人本体实验室，7×24 小时探索未知，产生全新的实验数据与科学发现，是突破边界、改变世界的内生动力。从 Cyber 到 Physical，从认知到实验，ScienceToken 让科学形成真正的闭环。"}
            </p>
          </div>

          {/* Video as hero */}
          <div className={cn(
            "rounded-2xl overflow-hidden border shadow-xl mb-10",
            isDark ? "border-white/10 shadow-black/40" : "border-slate-200 shadow-slate-300/30"
          )}>
            <video
              className="w-full aspect-video"
              autoPlay
              muted
              loop
              controls
              playsInline
            >
              <source src="/files/autonomous-lab.webm" type="video/webm" />
            </video>
          </div>

          {/* Supporting context */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: <Bot size={20} className="text-cyan-400" />,
                title: language === "en" ? "24/7 Autonomous Execution" : "7×24 小时自主执行",
                desc: language === "en"
                  ? "Robotic workstations run experiments around the clock, removing human bottlenecks from the R&D pipeline"
                  : "机器人工作站全天候运行实验，消除研发流程中的人工瓶颈",
                color: "cyan",
              },
              {
                icon: <Database size={20} className="text-blue-400" />,
                title: language === "en" ? "Standardized Data at Scale" : "规模化标准数据",
                desc: language === "en"
                  ? "Every experiment produces high-quality, fully traceable data — the fuel that makes our AI models increasingly accurate"
                  : "每次实验产出高质量、完全可追溯的数据——驱动 AI 模型持续提升精度的燃料",
                color: "blue",
              },
              {
                icon: <Workflow size={20} className="text-emerald-400" />,
                title: language === "en" ? "Closed-Loop Intelligence" : "闭环智能",
                desc: language === "en"
                  ? "From LLM reasoning to algorithmic computation to physical execution — the entire scientific workflow runs through a single platform"
                  : "从 LLM 推理到算法计算再到物理实验执行——整个科学工作流在同一个平台上贯通",
                color: "emerald",
              },
            ].map((item) => (
              <div
                key={item.title}
                className={cn(
                  "rounded-xl border p-5 transition-all duration-300",
                  isDark
                    ? `bg-${item.color}-500/5 border-white/8 hover:border-${item.color}-500/30`
                    : "bg-white border-slate-200 hover:border-slate-300"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center mb-3",
                  isDark ? "bg-white/5" : "bg-slate-50"
                )}>
                  {item.icon}
                </div>
                <p className={cn("text-sm font-semibold mb-2", isDark ? "text-white" : "text-slate-900")}>
                  {item.title}
                </p>
                <p className={cn("text-xs leading-relaxed", isDark ? "text-slate-400" : "text-slate-600")}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Mission ────────────────────────────────────────────────── */}
      <section className="py-24 bg-[var(--page-bg-alt)]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
              {language === "en" ? "Our Mission" : "我们的使命"}
            </Badge>
            <h2
              className={cn("text-3xl sm:text-4xl font-bold mb-4", isDark ? "text-white" : "text-slate-900")}
              style={{ fontFamily: "Space Grotesk, sans-serif" }}
            >
              {language === "en"
                ? "We serve the world's best. And we're opening the doors to everyone."
                : "我们服务于世界最顶尖的机构，同时向所有人敞开大门"}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Serving top institutions */}
            <div className={cn(
              "sci-card p-8 border-blue-500/20 hover:border-blue-500/40 transition-all duration-300",
              isDark ? "" : "bg-white"
            )}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <Building2 size={20} className="text-blue-400" />
                </div>
                <h3
                  className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                >
                  {language === "en" ? "Serving the World's Leading Institutions" : "服务全球顶级机构"}
                </h3>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: <Factory size={16} className="text-blue-400" />,
                    stat: "17 / 20",
                    desc: language === "en"
                      ? "of the world's top 20 pharmaceutical companies trust XtalPi"
                      : "全球前20大药企中的17家信赖晶泰科技",
                  },
                  {
                    icon: <Zap size={16} className="text-amber-400" />,
                    stat: language === "en" ? "World's #1" : "全球第一",
                    desc: language === "en"
                      ? "solar panel manufacturer -- strategic partnership for new materials"
                      : "光伏企业战略合作，加速新材料研发",
                  },
                  {
                    icon: <Building2 size={16} className="text-cyan-400" />,
                    stat: "BASF",
                    desc: language === "en"
                      ? "world's largest chemical company -- automated lab deployed globally"
                      : "全球最大化工企业，自动化实验室全球落地",
                  },
                  {
                    icon: <GraduationCap size={16} className="text-violet-400" />,
                    stat: "MIT",
                    desc: language === "en"
                      ? "XtalPi AI for Science Fellowship at MIT Department of Chemistry"
                      : "MIT化学系 XtalPi AI for Science Fellowship",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                      isDark ? "bg-white/5" : "bg-slate-50"
                    )}>
                      {item.icon}
                    </div>
                    <div>
                      <span
                        className={cn("text-sm font-bold", isDark ? "text-white" : "text-slate-900")}
                        style={{ fontFamily: "Space Grotesk, sans-serif" }}
                      >
                        {item.stat}
                      </span>
                      <span className={cn("text-sm ml-2", isDark ? "text-slate-400" : "text-slate-600")}>
                        {item.desc}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Science for everyone */}
            <div className={cn(
              "sci-card p-8 border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300 relative overflow-hidden",
              isDark ? "" : "bg-white"
            )}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -translate-y-1/3 translate-x-1/3 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                    <Microscope size={20} className="text-emerald-400" />
                  </div>
                  <h3
                    className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-900")}
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {language === "en" ? "Making Science Accessible to Everyone" : "让科学平权，人人可用"}
                  </h3>
                </div>

                <blockquote className={cn(
                  "text-xl font-bold italic mb-5 leading-tight",
                  isDark ? "text-white" : "text-slate-900"
                )} style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  {language === "en"
                    ? '"The next scientist could be anyone."'
                    : '"下一位科学家，可以是任何人。"'}
                </blockquote>

                <p className={cn("text-sm leading-relaxed mb-5", isDark ? "text-slate-300" : "text-slate-600")}>
                  {language === "en"
                    ? "Just as vibe coding lets anyone build software with natural language, ScienceToken empowers everyone to explore the frontiers of science the same way — describe your scientific goal in plain language, and let AI handle the computation."
                    : "就像 vibe coding 让每个人都能用自然语言构建软件一样，ScienceToken 让每个人都能以同样的方式探索科学边界——用自然语言描述你的科学目标，让 AI 完成计算。"}
                </p>
                <p className={cn("text-sm leading-relaxed mb-6", isDark ? "text-slate-400" : "text-slate-500")}>
                  {language === "en"
                    ? "No PhD required. No wet lab needed. Just curiosity, a question, and ScienceToken. Whether you're predicting molecular properties, designing new compounds, or analyzing crystal structures — the same capabilities trusted by the world's top institutions are now yours."
                    : "不需要博士学位，不需要湿实验室。只需要好奇心、一个问题和 ScienceToken。无论是预测分子性质、设计新化合物还是分析晶体结构——全球顶级机构信赖的同款能力，现在为你所用。"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Scientific Renaissance Vision ────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 sci-bg-grid opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent" />

        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs tracking-widest uppercase text-slate-600 mb-6">
            {language === "en" ? "Our Vision" : "我们的愿景"}
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 max-w-[900px] mx-auto"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            {language === "en" ? (
              <>
                We are igniting an{" "}
                <span className="sci-gradient-text">AI-powered</span>
                <br />
                scientific renaissance
              </>
            ) : (
              <>
                我们正在点燃一场{" "}
                <span className="sci-gradient-text">AI 驱动的</span>
                <br />
                科学大爆发
              </>
            )}
          </h2>
          <p className={cn("text-lg max-w-[640px] mx-auto mb-10", isDark ? "text-slate-400" : "text-slate-600")}>
            {language === "en"
              ? "ScienceToken is not just a platform. It is the infrastructure for science's new Age of Discovery."
              : "ScienceToken 不只是一个平台，它是科学大航海时代的基础设施"}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/console/keys">
              <Button size="lg" className="btn-primary-sci gap-2 px-8 h-12 font-semibold text-base">
                {t("home.cta.free")}
                <ArrowRight size={16} />
              </Button>
            </Link>
            <Link href="/docs">
              <Button size="lg" variant="ghost" className="btn-outline-sci gap-2 px-8 h-12">
                {t("home.cta.docs")}
                <ExternalLink size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
